import { getI18n } from '@/plugins/i18n'

import { useProductService } from '@/services/product.service'

import * as dataGridColumn from '@/composables/data-grid-column'
import { useWebSiteStore } from '@/stores/web-site.store'

export const useColumns = (supplierBrand = {}, keepDescription, currency, documentRowType, saleDocumentCustomer = {}, supersessionId = {}, documentRowProductPurchaseFamily = {}, includesVat = {}, productUnitOfMeasure = {}, laborPriceContext = {}) => {
  const i18n = getI18n()
  const { setTotalVatValue, setTotalNoVatValue, setPriceVatValue } = useProductService()
  const { useRoundedTotalColumn, useRoundingColumn, useRoundingPercentageColumn, useRoundingDocumentTotalVatColumn } = dataGridColumn.useRoundingExpenseColumn(documentRowProductPurchaseFamily, includesVat)
  const webSiteStore = useWebSiteStore()

  return [
    {
      type: 'buttons',
      buttons: [{template: 'editButtonsTemplate'}],
      width: '4rem',
      visibleIndex: 1,
      fixed: false
    },
    dataGridColumn.useRowNumberColumn({width: '8rem'}),
    dataGridColumn.useDocumentRowTypeColumn({
      editCellTemplate: 'documentRowType',
      cellTemplate: 'documentRowTypeCellTemplate',
      productUnitOfMeasure: productUnitOfMeasure,
    }),
    dataGridColumn.useCostColumn({validationRules: []}),
    dataGridColumn.useCostValorizationColumn({ priceContext: laborPriceContext }),
    dataGridColumn.useProcessingTypeColumn(),
    {
      dataField: 'quote.id',
      caption: i18n.t('common.quoteRow'),
      width: '8rem',
      editCellTemplate: 'quoteRowRef',
      cellTemplate: 'quoteRowRef',
      alignment: 'left'
    },
    dataGridColumn.useSupplierBrandColumn({ supplierBrand, documentRowType }),
    ...dataGridColumn.useProductColumns({ supplierBrand, keepDescription, customer: saleDocumentCustomer, supersessionId, productUnitOfMeasure, priceContext: laborPriceContext }),
    dataGridColumn.useDescriptionColumn({ validationRules: [{ type: 'required' }] }),
    dataGridColumn.useIsMovementColumn(),
    dataGridColumn.useQuantityColumn(),
    dataGridColumn.usePriceColumn({ dataField: 'product_price', caption: i18n.t('common.listing'), currency, validationRules: [], visible: false, showInColumnChooser: false }),
    dataGridColumn.usePriceColumn({ caption: i18n.t('common.unitPriceNoVat'), currency }),
    dataGridColumn.usePriceColumn({
      dataField: 'price_vat',
      caption: i18n.t('common.unitPriceVat'),
      currency,
      setCellValue: (newData, value, currentRowData) => {
        setPriceVatValue(newData, value, currentRowData)
      }
    }),
    dataGridColumn.useDiscountCategoryColumn(),
    ...dataGridColumn.useVatTypeColumns({
      validationRules: [
        {
          type: 'custom',
          message: i18n.t('common.required'),
          reevaluate: true,
          validationCallback: (e) => {
            const type = e.data.document_row_type
            const isRequiredType = type === webSiteStore.documentRowTypesEnum.PRODUCT || type === webSiteStore.documentRowTypesEnum.FREE || type === webSiteStore.documentRowTypesEnum.LABOR || type === webSiteStore.documentRowTypesEnum.EXPENSE || type === webSiteStore.documentRowTypesEnum.SERVICE
            const hasValue = !!e.value
            return hasValue || !isRequiredType
          }
        }
      ]
    }),
    dataGridColumn.useTotalNoVatColumn({ currency, setCellValue: (newData, value, currentRowData) => { setTotalNoVatValue(newData, value, currentRowData) } }),
    dataGridColumn.useTotalVatColumn({ currency, setCellValue: (newData, value, currentRowData) => { setTotalVatValue(newData, value, currentRowData, includesVat.value) } }),
    dataGridColumn.useProcessingColumn(),
    dataGridColumn.useProcessingParentColumn(),
    dataGridColumn.useNotesColumn(),
    dataGridColumn.useDiscountColumn({includesVat: includesVat.value }),
    dataGridColumn.useDiscount2Column({includesVat: includesVat.value }),
    dataGridColumn.useDiscount3Column({includesVat: includesVat.value }),
    {
      dataField: 'total_discount',
      dataType: 'number',
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useChargePercentageColumn({ caption: i18n.t('common.chargePercentageAlt') }),
    dataGridColumn.useUnitOfMeasureColumn({
      showClearButton: false,
      productUnitOfMeasure: productUnitOfMeasure,
      validationRules: [
        {
          type: 'custom',
          message: i18n.t('common.required'),
          reevaluate: true,
          validationCallback: (e) => {
            const type = e.data.document_row_type
            const isRequiredType = type === webSiteStore.documentRowTypesEnum.PRODUCT || type === webSiteStore.documentRowTypesEnum.FREE
            const hasValue = !!e.value
            return hasValue || !isRequiredType
          }
        }
      ]
    }),
    dataGridColumn.useDepositColumn({visible: false, showInColumnChooser: false, validationRules: []}),
    dataGridColumn.useDepositColumn({
      dataField: 'garage',
      caption: i18n.t('common.garage'),
      visible: false,
      showInColumnChooser: false,
      validationRules: []}
    ),
    {
      caption: i18n.t('common.timeEquivalent'),
      dataField: 'timeEquivalent',
      editCellTemplate: 'timeEquivalentEditor',
      editorOptions: { readOnly: true },
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.actualDuration'),
      dataField: 'actual_duration',
      editorOptions: { readOnly: true },
      dataType: 'number',
      width: '5rem',
    },
    {
      caption: i18n.t('common.suggestedTime'),
      dataField: 'suggestedTime',
      editorOptions: { readOnly: true },
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.operationCode'),
      dataField: 'operationCode',
      editorOptions: { readOnly: true },
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.recognizedTime'),
      dataField: 'recognizedTime',
      editorOptions: { readOnly: true },
      visible: false,
      showInColumnChooser: false,
    },
    useRoundedTotalColumn({ currency }),
    useRoundingColumn({ currency }),
    useRoundingPercentageColumn(),
    useRoundingDocumentTotalVatColumn({ currency }),
  ]
}
